"use client";

import { useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase";
import { isNativeApp } from "@/lib/native/platform";
import { buildGoogleCallbackUrl, type GoogleAuthContext } from "@/lib/google-auth";
import { getSignupCampaignAttribution } from "@/lib/campaign-attribution-client";

export function GoogleSignInButton({ disabled = false, ...context }: GoogleAuthContext & { disabled?: boolean }) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const starting = useRef(false);

  useEffect(() => {
    // Google rejects embedded WebViews. Native sign-in needs its own SDK flow.
    if (isNativeApp()) return;
    const controller = new AbortController();
    void fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/auth/v1/settings`, {
      headers: { apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY! },
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return;
      const settings: { external?: { google?: boolean } } = await response.json();
      if (!controller.signal.aborted) setEnabled(settings.external?.google === true);
    }).catch(() => {
      // Email sign-in remains available if provider discovery is unavailable.
    });
    return () => controller.abort();
  }, []);

  async function signIn() {
    if (starting.current) return;
    starting.current = true;
    setLoading(true);
    setError(null);
    try {
      const { data, error: authError } = await createClient().auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: buildGoogleCallbackUrl(window.location.origin, {
            ...context,
            campaignAttribution: getSignupCampaignAttribution(context.campaignAttribution),
          }),
          skipBrowserRedirect: true,
        },
      });
      if (authError || !data.url) throw new Error("Google sign-in could not start.");
      window.location.assign(data.url);
    } catch {
      setError("We could not connect to Google. Please try again.");
      setLoading(false);
      starting.current = false;
    }
  }

  if (!enabled) return null;

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={signIn}
        disabled={disabled || loading}
        className="flex min-h-12 w-full items-center justify-center gap-3 rounded-[12px] border border-border bg-white px-4 py-3 text-sm font-medium text-charcoal transition hover:bg-warm-bg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy disabled:cursor-wait disabled:opacity-60"
      >
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 48 48" className="shrink-0">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65Z" />
          <path fill="#FBBC05" d="M10.53 28.59A14.41 14.41 0 0 1 9.75 24c0-1.59.27-3.13.78-4.59l-7.98-6.19A23.87 23.87 0 0 0 0 24c0 3.87.93 7.53 2.56 10.78l7.97-6.19Z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.91-5.8l-7.73-6c-2.15 1.45-4.92 2.3-8.18 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48Z" />
        </svg>
        {loading ? "Connecting to Google…" : "Continue with Google"}
      </button>
      {error ? <p role="alert" className="mt-3 text-sm text-error">{error}</p> : null}
      <div className="mt-6 flex items-center gap-3 text-xs text-text-muted">
        <span className="h-px flex-1 bg-border-light" />
        <span>or continue with email</span>
        <span className="h-px flex-1 bg-border-light" />
      </div>
    </div>
  );
}
